import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Modal from "../elements/Modal";
import { useAuthContext } from "../../contexts/authContext";
import {
  acceptSupervisionInvite,
  declineSupervisionInvite,
} from "../../api/supervision";
import { USER_TYPES } from "../../constants/UserConstants";
import { useMyProjects } from "../../hooks/useMyProjects";
import { useNotificationsContext } from "../../contexts/notificationsContext";

const SupervisionInvite = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [isUserNew, setIsUserNew] = useState(true);
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const { isAuthenticated, user, logout } = useAuthContext();

  const { fetchProjects } = useMyProjects();

  const { markSupervisionInviteNotificationAsRead } = useNotificationsContext();

  const navigateWithReplace = useCallback(
    (route: string) => navigate(route, { replace: true }),
    [navigate]
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const t = urlParams.get("t");
    const e = urlParams.get("e");
    const n = urlParams.get("n");
    const ex = urlParams.get("ex");

    if (!t || !e || !n || !ex) {
      navigateWithReplace("/home");
      return;
    }

    localStorage.setItem("supervisorInviteToken", t);
    setToken(t);

    if (e) {
      setEmail(e);
    }

    if (n) {
      setIsUserNew(n === "yes");
    }

    if (ex) {
      setExpirationDate(new Date(ex));
    }

    setIsModalOpen(true);
  }, [navigateWithReplace]);

  const handleLogout = async (navigationRoute: string) => {
    if (isAuthenticated) {
      await logout();
    }
    navigate(navigationRoute, { replace: true });
  };

  const handleAcceptNotAuthenticated = () => {
    if (isUserNew) {
      navigateWithReplace(`/register?email=${email}`);
    } else {
      navigateWithReplace(`/login?email=${email}`);
    }
  };

  const handleAccept = async () => {
    setAccepting(true);

    const { data, error } = await acceptSupervisionInvite(token);

    if (data) {
      const projectId = data?.project?.id || data?.projectId;
      if (typeof projectId === "number") {
        localStorage.removeItem("supervisorInviteToken");
      } else {
      }

      await markSupervisionInviteNotificationAsRead(data.id);
      await fetchProjects();
    }

    if (error) {
      toast.error(error.error);
    }

    setAccepting(false);
    navigateWithReplace("/projects");
  };

  const handleDecline = async (navigateRoute: string) => {
    try {
      const { data, error } = await declineSupervisionInvite(token);

      if (data) {
        localStorage.removeItem("supervisorInviteToken");
        await markSupervisionInviteNotificationAsRead(data.id);
      }
      if (error) {
        toast.error(error.error);
      }
    } catch (err) {
      toast.error("Something unexpected happened!");
    } finally {
      navigateWithReplace(navigateRoute);
    }
  };

  if (expirationDate && new Date() > expirationDate) {
    return (
      <Modal isOpen={isModalOpen} sidebarOffset={isAuthenticated}>
        <div className="p-6 flex flex-col">
          <h2 className="font-bold text-xl mb-1">
            Invitation to supervise a Project on Build Connect
          </h2>
          <p className="mb-6 text-gray-700">{`The invitation has expired.`}</p>
          <div className="flex flex-row gap-4">
            <button
              className="w-full bg-red-600 text-white px-4 py-2 rounded-md"
              onClick={() =>
                isAuthenticated
                  ? navigateWithReplace("projects")
                  : navigateWithReplace("/home")
              }
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  if (
    isAuthenticated &&
    (user?.email !== email || user?.type !== USER_TYPES.SUPERVISOR)
  ) {
    return (
      <Modal isOpen={isModalOpen} sidebarOffset={isAuthenticated}>
        <div className="p-6 flex flex-col">
          <h2 className="font-bold text-xl mb-1">
            Invitation to supervise a Project on Build Connect
          </h2>
          <p className="mb-6 text-gray-700">
            {`This invitation was sent to ${email}, but you are currently logged in as ${user?.email}.`}{" "}
            {isUserNew &&
              `To accept or decline this invitation, please log out and click the invite link again.`}
            {!isUserNew &&
              `To accept or decline this invitation, please log in with ${email} and click the invite link again or click on the received notification.`}
          </p>
          <div className="flex flex-row gap-4">
            {isUserNew && (
              <button
                className="w-full bg-buttonBlue text-white px-4 py-2 rounded-md"
                onClick={() => handleLogout("/home")}
              >
                Logout
              </button>
            )}
            {!isUserNew && (
              <button
                className="w-full bg-buttonBlue text-white px-4 py-2 rounded-md"
                onClick={() => handleLogout("/login")}
              >
                Logout
              </button>
            )}
            <button
              className="w-full bg-red-600 text-white px-4 py-2 rounded-md"
              onClick={() => navigateWithReplace("/projects")}
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isModalOpen} sidebarOffset={isAuthenticated}>
      <div className="p-6 flex flex-col">
        <h2 className="font-bold text-xl mb-1">You've been invited!</h2>
        <p className="mb-6 text-gray-700">
          You’ve been invited to supervise a project on Build Connect.
        </p>
        <div className="flex flex-row gap-4">
          {!user && isUserNew && (
            <button
              className="w-full bg-buttonBlue text-white px-4 py-2 rounded-md"
              onClick={handleAcceptNotAuthenticated}
            >
              Register & Accept
            </button>
          )}
          {!user && !isUserNew && (
            <button
              className={`w-full bg-buttonBlue text-white px-4 py-2 rounded-md ${
                accepting ? "opacity-50" : ""
              }`}
              onClick={handleAcceptNotAuthenticated}
              disabled={accepting}
            >
              Login & Accept
            </button>
          )}
          {user && (
            <button
              className="w-full bg-buttonBlue text-white px-4 py-2 rounded-md"
              onClick={handleAccept}
            >
              Accept
            </button>
          )}
          <button
            className="w-full bg-red-600 text-white px-4 py-2 rounded-md"
            onClick={() =>
              handleDecline(isAuthenticated ? "/projects" : "/home")
            }
          >
            Decline
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SupervisionInvite;
